// CobrancaCartao — PEÇA ÚNICA (016) de cobrança no CARTÃO pela maquininha Mercado Pago Point (modo PDV).
// Irmã da <CobrancaPix> (013): MESMA forma de leitura, ciclo diferente (o cliente paga PRESENCIAL na
// maquininha, sem QR / tela de cliente). Atende CRÉDITO e DÉBITO numa peça só — o tipo é só um parâmetro.
// Faz o CICLO COMPLETO de UMA cobrança e devolve a bola pra quem chamou:
//   monta (COBRA AO MONTAR) → manda o valor pro terminal → "na maquininha" → acompanha (poll 4s +
//   socket `cartao:aprovado`, só na venda) → APROVADO  ·OU·  recusado/expirado (Tentar de novo) → devolve.
// Venda: avisa onAprovado pra a finalização liberar a nota (a trava anti-erro do pai lê isso). Avulso:
// mostra "recebimento livre". Quem usa NÃO enxerga o estado por dentro — monta o componente quando decide
// cobrar (o gatilho "Cobrar na maquininha" vive no pai) e reage aos avisos (onAprovado / onCancelado).
//
// Débito é sempre à vista (força 1x; sem imposto/juros). Crédito parcelado: o backend embute imposto e o
// MP soma o juros na parcela do cliente — aqui mandamos só o principal + a parcela escolhida (DA-209/210).
// GOTCHA (DA-240): a Point Integration API NÃO deixa forçar débito — débito 1x manda o MESMO intent do
// crédito 1x e o CLIENTE escolhe débito no terminal. O card_type é só rótulo pro backend.
//
// Cores 100% por tokens do tema; destaque positivo amarelo (#fde68a + preto), nunca verde/vermelho como
// ÚNICO sinal (daltonismo). Ver catálogo de componentes (Configurações → Catálogo).
import { useCallback, useEffect, useRef, useState } from 'react';
import { Check, Loader2, CreditCard, AlertTriangle } from 'lucide-react';
import { formatMoeda } from '@fiodecobre/shared';
import { api } from '../../lib/api';
import { useSocket } from '../../lib/socket';
import { useToast } from '../Toast';
import { useDialog } from '../DialogProvider';

// ALVO da cobrança: venda (amarrada a um orçamento; o "aprovado" libera a finalização da nota), avulso
// (amarrada a um cliente; o recebimento fica LIVRE pra abater na mão em Contas a Receber) OU título
// (cobra um título de crediário no Contas a Receber: cobra como avulso — gera o recebimento livre —, e
// quem usa casa a baixa do título sozinho com o recebimento_id devolvido no onAprovado).
export type AlvoCobrancaCartao =
  | { tipo: 'venda'; orcamentoId: number }
  | { tipo: 'avulso'; clienteId: number }
  | { tipo: 'titulo'; clienteId: number; contaReceberId?: number };

// na_maquininha (esperando o cliente pagar) → aprovado. recusado/cancelado/expirado = tentar de novo.
// 'errada' = o cartão PASSOU, mas o parcelamento não se confirmou como Parcelado Cliente (DA-516):
// não há "tentar de novo" (o cliente já foi debitado) e não pode virar aprovado (a venda não fecha).
type Status = 'na_maquininha' | 'aprovado' | 'recusado' | 'cancelado' | 'expirado' | 'errada';

interface Props {
  alvo: AlvoCobrancaCartao;
  /** Valor (principal) mandado pro terminal. No crédito parcelado o backend embute imposto/juros. */
  valor: number;
  /** 'credit' | 'debit'. Débito força 1x. */
  cardType: 'credit' | 'debit';
  /** Parcelas — só crédito (venda). Débito/avulso = 1. */
  parcelas?: number;
  /** 'inline' (Finalizar Venda, faixa ao lado) | 'cheio' (Receber Avulso, centralizado). */
  layout: 'inline' | 'cheio';
  /** Total a EXIBIR quando aprovado (parcelado = preço de cartão + juros). Default = valor. Só visual. */
  totalAprovado?: number;
  /** Só parcelado: o valor que SOBE pro device (preço de cartão + imposto, sem os juros). É o que a
   *  maquininha mostra na tela da SENHA ([DA-808]) — o aviso de espera cita ele pra o balcão não ler
   *  "o valor caiu" e cancelar uma cobrança certa. */
  valorNaSenha?: number;
  /** Disparado na TRANSIÇÃO pra aprovado (uma vez). No alvo 'titulo'/'avulso' vem o id do recebimento
   *  livre (recebimento_avulso_id) gerado — quem usa casa a baixa do título com ele. A venda usa o
   *  2º argumento (id da cobrança) pra contar a paga em `pagasConhecidas` ([DA-775]). */
  onAprovado?: (recebimentoId?: number | null, cobrancaId?: number | null) => void;
  /** Só venda: ids das cobranças JÁ PAGAS deste pedido que a tela já contou. O backend recusa cobrar
   *  se houver paga fora desta lista — é a cobrança dupla que a tela não viu chegar ([DA-775]). */
  pagasConhecidas?: number[];
  /** Cobrança cancelada (pelo operador, falha ao criar, ou terminal ocupado) — quem usa volta o estado. */
  onCancelado?: () => void;
  /** A maquininha imprime o comprovante? Padrão NÃO — só quando o cliente pede a via. Vai junto com a
   *  cobrança, então tem que ser escolhido ANTES (caixinha <ImprimirViaCartao> ao lado do gatilho). */
  imprimirVia?: boolean;
}

// Caixinha "Imprimir via do cliente" — fica ao lado de todo gatilho que cobra na maquininha. Nasce
// DESMARCADA: o papel só sai quando o cliente pede (pedido do Ruan, 05/10/2026).
export function ImprimirViaCartao({ marcado, onChange }: { marcado: boolean; onChange: (v: boolean) => void }) {
  return (
    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer', textTransform: 'none', userSelect: 'none' }}>
      <input type="checkbox" checked={marcado} onChange={(e) => onChange(e.target.checked)} />
      Imprimir via do cliente na maquininha
    </label>
  );
}

export function CobrancaCartao({ alvo, valor, cardType, parcelas = 1, layout, totalAprovado, valorNaSenha, onAprovado, onCancelado, pagasConhecidas, imprimirVia = false }: Props) {
  const { socket } = useSocket();
  const toast = useToast();
  const dialog = useDialog();
  const cheio = layout === 'cheio';
  // Números da cobrança errada (DA-516): quanto o cliente pagou contra quanto deveria.
  const [erroParcelamento, setErroParcelamento] = useState<{ pago: number | null; esperado: number | null } | null>(null);
  // Total REALMENTE pago, lido do Mercado Pago na aprovação. O selo "Aprovado" mostrava a
  // ESTIMATIVA calculada antes de cobrar (`totalAprovado`) — então ele afirmava R$ 191,33 mesmo
  // quando tinham entrado R$ 171,68. O valor pago nem trafegava pra tela.
  const [pagoReal, setPagoReal] = useState<number | null>(null);

  const [cobranca, setCobranca] = useState<{ cobranca_id: number; valor: string } | null>(null);
  const [status, setStatus] = useState<Status>('na_maquininha');
  const [cobrando, setCobrando] = useState(false);
  // Contador "enviado ha Ns" enquanto espera a maquininha. Medido em 10/10/2026: metade das cobrancas
  // pagas leva ate 18s, mas 1 em 10 passa de 41s e 1 em 20 de 1min13 — e no balcao se desistia aos
  // 15-35s, devolvendo o pedido com a cobranca ainda viva (ORC-008844 foi pago DEPOIS de devolvido).
  const [enviadoEm, setEnviadoEm] = useState<number | null>(null);
  const [agora, setAgora] = useState(() => Date.now());
  useEffect(() => {
    if (status !== 'na_maquininha' || enviadoEm == null) return;
    setAgora(Date.now());
    const t = setInterval(() => setAgora(Date.now()), 1000);
    return () => clearInterval(t);
  }, [status, enviadoEm]);
  const segundosEnviado = enviadoEm != null ? Math.max(0, Math.floor((agora - enviadoEm) / 1000)) : null;
  const tempoEnviado = segundosEnviado == null ? ''
    : segundosEnviado < 60 ? `${segundosEnviado}s`
    : `${Math.floor(segundosEnviado / 60)}min${String(segundosEnviado % 60).padStart(2, '0')}`;
  // Ate 1 min: tranquiliza. Depois: diz o que NAO fazer (devolver o pedido deixa a cobranca viva) e
  // qual e a saida certa — o Cancelar ao lado.
  const avisoEspera = segundosEnviado == null ? null
    : segundosEnviado < 60
      ? `Enviado à maquininha há ${tempoEnviado} — pode levar até 1 minuto para aparecer.`
      : `Enviado à maquininha há ${tempoEnviado}. Se o cliente pagar, o valor fica neste pedido — para desistir, use Cancelar (não devolva o pedido).`;

  // Callbacks num ref estável — usados dentro de poll/socket sem re-assinar a cada render.
  const cbRef = useRef({ onAprovado, onCancelado });
  cbRef.current = { onAprovado, onCancelado };
  const pagasRef = useRef(pagasConhecidas ?? []);
  pagasRef.current = pagasConhecidas ?? [];

  // Rotas por alvo (a venda amarra no orçamento; o avulso, no cliente). tipo é estável.
  const venda = alvo.tipo === 'venda';
  const rotaCobrar = venda ? '/api/cartao/cobrar' : '/api/receber-avulso/cartao/cobrar';
  const rotaStatus = (id: number) => (venda ? `/api/cartao/cobranca/${id}` : `/api/receber-avulso/cartao/cobranca/${id}`);
  const rotaCancelar = (id: number) => (venda ? `/api/cartao/cobranca/${id}/cancelar` : `/api/receber-avulso/cartao/cobranca/${id}/cancelar`);

  // Marca aprovada e dispara onAprovado só na TRANSIÇÃO (não estava aprovada → aprovada). Carrega o
  // recebimento_id (a linha livre criada) pra quem cobra por título casar a baixa sozinho.
  const recebidoIdRef = useRef<number | null>(null);
  const aprovadoDisparadoRef = useRef(false);
  const marcarAprovado = useCallback((recebimentoId?: number | null) => {
    if (recebimentoId != null) recebidoIdRef.current = recebimentoId;
    setStatus((s) => (s === 'aprovado' ? s : 'aprovado'));
    if (!aprovadoDisparadoRef.current) {
      aprovadoDisparadoRef.current = true;
      const recId = recebidoIdRef.current;
      const cobId = cobrancaIdRef.current;
      queueMicrotask(() => cbRef.current.onAprovado?.(recId, cobId));
    }
  }, []);

  // Manda o valor pro terminal. Usado no COBRA AO MONTAR e no "Tentar de novo" (cria nova cobrança).
  const cobrar = useCallback(async () => {
    if (!(valor > 0)) { toast.erro('Valor do cartão inválido'); cbRef.current.onCancelado?.(); return; }
    setCobrando(true);
    try {
      const corpo = venda
        ? { orcamento_id: alvo.orcamentoId, valor, parcelas: cardType === 'debit' ? 1 : Math.max(1, parcelas || 1), card_type: cardType, pagas_conhecidas: pagasRef.current, imprimir_via: imprimirVia }
        : { cliente_id: alvo.clienteId, valor, card_type: cardType, imprimir_via: imprimirVia };
      const r = await api.post<{ cobranca_id: number; valor: string }>(rotaCobrar, corpo);
      setCobranca(r);
      setEnviadoEm(Date.now());
      setStatus('na_maquininha');
      setCobrando(false);
      toast.sucesso('Valor enviado pra maquininha — peça o cartão ao cliente');
    } catch (e: any) {
      setCobrando(false);
      // [DA-775] Cartão já aprovado que esta tela não viu, ou a cobrança anterior DESTE pedido ainda
      // na maquininha: janela, não toast — a instrução ("não cobre de novo, reabra") tem que ficar
      // na tela até alguém ler, senão o reflexo é clicar em cobrar outra vez.
      const motivo = e?.corpo?.motivo;
      if (motivo === 'ja_pago' || motivo === 'mesmo_pedido') {
        void dialog.alerta(e.message, {
          titulo: motivo === 'ja_pago' ? 'Este pedido já foi pago no cartão' : 'A maquininha está com o valor deste pedido',
          tipo: 'erro',
        });
        cbRef.current.onCancelado?.();
        return;
      }
      // 409 = terminal em uso (já há uma cobrança aberta). Avisa e devolve o controle.
      if (e?.status === 409) toast.erro(e.message || 'Maquininha em uso — aguarde a cobrança atual terminar');
      else toast.erro(e?.message || 'Falha ao enviar pra maquininha');
      cbRef.current.onCancelado?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valor, cardType, parcelas, imprimirVia]);

  // --- COBRA AO MONTAR ---  (cobrouRef evita o duplo-disparo do StrictMode em dev)
  const cobrouRef = useRef(false);
  useEffect(() => {
    if (cobrouRef.current) return;
    cobrouRef.current = true;
    void cobrar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Confere o desfecho de UMA cobrança. Poll e socket passam os dois por aqui de propósito: o socket
  // só carrega o id, então aprovar direto nele pularia a checagem do parcelamento — e a regra ficaria
  // escrita em dois lugares, que já falhou por definição ([DA-463]).
  const conferirDesfecho = useCallback(async (id: number) => {
    const r = await api.get<{
      status: string; recebimento_id?: number | null;
      parcelamento_custo?: string | null; valor_pago?: string | null; valor_esperado?: string | null;
    }>(rotaStatus(id));
    if (r.status === 'pago') {
      // PARCELADO TEM QUE SER PELO CLIENTE (DA-516). Recusa por AUSÊNCIA DE CONFIRMAÇÃO, não por
      // prova de culpa: qualquer coisa que não seja 'cliente' trava. Isso fecha de graça o caso em
      // que o MP não devolve o total pago — aí o sistema não sabe, e não sabendo não deixa passar.
      const parceladoOk = !venda || (parcelas ?? 1) < 2 || r.parcelamento_custo === 'cliente';
      if (!parceladoOk) {
        setErroParcelamento({
          pago: r.valor_pago != null ? Number(r.valor_pago) : null,
          esperado: r.valor_esperado != null ? Number(r.valor_esperado) : null,
        });
        setStatus('errada');
        // Modal, não toast: é irreversível (o cartão já foi debitado) e o texto tem que sobreviver
        // na tela até alguém ler — [DA-443].
        void dialog.alerta(
          'Cobrança errada,\n'
          + 'deveria ser Parcelado pelo Cliente e foi Parcelado pelo Vendedor,\n'
          + 'Favor solicitar o Estorno com a Administração!',
          { titulo: 'Cobrança errada', tipo: 'erro' },
        );
        return;
      }
      if (r.valor_pago != null) setPagoReal(Number(r.valor_pago));
      marcarAprovado(r.recebimento_id ?? null);
      return;
    }
    if (r.status === 'recusado' || r.status === 'cancelado' || r.status === 'expirado') setStatus(r.status as Status);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [venda, parcelas, marcarAprovado]);

  // --- POLL a cada 4s: confere o status no backend (a Point limita 1 req/s; 4s é seguro) ---
  useEffect(() => {
    if (!cobranca || status !== 'na_maquininha') return;
    const id = cobranca.cobranca_id;
    const t = setInterval(() => {
      void conferirDesfecho(id).catch(() => { /* silencioso — rede de segurança */ });
    }, 4000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cobranca, status, conferirDesfecho]);

  // --- SOCKET (só na venda): o backend emite `cartao:aprovado` quando o intent aprova (webhook/poll) ---
  useEffect(() => {
    if (!socket || !cobranca || !venda) return;
    const id = cobranca.cobranca_id;
    const onAprov = (d: { cobranca_id: number; orcamento_id: number }) => {
      if (d.cobranca_id === id) void conferirDesfecho(id).catch(() => {});
    };
    socket.on('cartao:aprovado', onAprov);
    return () => { socket.off('cartao:aprovado', onAprov); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket, cobranca, conferirDesfecho]);

  // Cancela a cobrança ativa (cliente desistiu) — apaga o valor da maquininha e devolve a bola.
  // Libera mesmo se a rede falhar (o backend reabre o terminal sozinho em ~10 min).
  const cancelar = useCallback(async () => {
    if (cobranca) {
      try {
        await api.post(rotaCancelar(cobranca.cobranca_id), {});
      } catch (e: any) {
        const motivo = e?.corpo?.motivo;
        // ⛔ O Mercado Pago RECUSOU o cancelamento porque o valor está NA MÃO DO CLIENTE. Dar a
        // cobrança por cancelada aqui é o que fez a venda do ORC-007775 virar dinheiro órfão
        // ([DA-718]) — e ainda oferece "Tentar de novo", que é cobrança dupla. Fica onde está,
        // com o poll rodando: se o cliente aproximar o cartão, a tela aprova sozinha.
        if (motivo === 'na_maquininha') {
          void dialog.alerta(
            e.message || 'A maquininha está com o valor. Conclua ou cancele NA MAQUININHA.',
            { titulo: 'A maquininha está com o valor', tipo: 'erro' },
          );
          return;
        }
        // Já tinha sido pago: o backend acabou de registrar. Puxa o desfecho pelo caminho normal
        // (que confere o parcelamento) em vez de fechar como cancelada.
        if (motivo === 'pago') {
          await conferirDesfecho(cobranca.cobranca_id).catch(() => {});
          return;
        }
        /* qualquer outro erro (rede): segue liberando a tela, como antes */
      }
    }
    setStatus('cancelado');
    cbRef.current.onCancelado?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cobranca, conferirDesfecho, dialog]);

  // Cancela no DESMONTE se ainda na maquininha — sumir o componente (trocar a forma/valor, remover a
  // linha, fechar o modal) nunca pode deixar uma cobrança presa no terminal. Aqui SÓ o POST (sem
  // setState/callback num componente que já saiu de cena). O botão "Cancelar" cuida do caso com a peça viva.
  const cobrancaIdRef = useRef<number | null>(null);
  cobrancaIdRef.current = cobranca?.cobranca_id ?? null;
  const statusRef = useRef(status);
  statusRef.current = status;
  useEffect(() => () => {
    if (statusRef.current === 'na_maquininha' && cobrancaIdRef.current) {
      void api.post(rotaCancelar(cobrancaIdRef.current), {}).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // =========================== VISUAL ===========================

  // --- APROVADO ---
  if (status === 'aprovado') {
    // O que ENTROU tem prioridade sobre o que foi estimado (DA-516). A estimativa só aparece
    // enquanto o valor real não chegou — afirmar um número que ninguém cobrou é pior que não
    // mostrar número nenhum.
    const total = pagoReal ?? totalAprovado ?? valor;
    if (cheio) {
      return (
        <div data-componente="016" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, padding: '12px 0' }}>
          <div style={{ width: 90, height: 90, borderRadius: '50%', background: '#15803d', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Check size={52} color="#fff" strokeWidth={3} />
          </div>
          <div style={{ fontSize: 18, fontWeight: 800, color: '#166534' }}>CARTÃO APROVADO</div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', textAlign: 'center', maxWidth: 320 }}>
            {alvo.tipo === 'titulo'
              ? <>Título <strong>baixado e conciliado</strong> com este cartão.</>
              : <>Esse valor ficou <strong>livre</strong>. Abata nos títulos do cliente em <strong>Contas a Receber</strong> (baixa parcial → Vincular ao recebimento).</>}
          </div>
        </div>
      );
    }
    return (
      <div data-componente="016" style={{ display: 'flex', alignItems: 'center', gap: 10, background: '#bfdbfe', borderLeft: '5px solid #1d4ed8', borderRadius: 8, padding: '10px 14px', color: '#000', fontWeight: 700, maxWidth: 520 }}>
        <Check size={20} /> Aprovado na maquininha — {formatMoeda(total)}{parcelas > 1 ? ` (${parcelas}x de ${formatMoeda(total / parcelas)})` : ''}
      </div>
    );
  }

  // --- COBRANÇA ERRADA (parcelado que não se confirmou como Cliente) ---
  // Sem "Tentar de novo": o cartão do cliente JÁ foi debitado, então repetir cobraria duas vezes. A
  // faixa fica na tela (não é toast) porque a venda não vai fechar e alguém precisa ler o motivo.
  // Laranja sólido com fonte preta — a cor de atenção da casa, nunca verde/vermelho (daltonismo).
  if (status === 'errada') {
    const pago = erroParcelamento?.pago ?? null;
    const esperado = erroParcelamento?.esperado ?? null;
    const juros = pago != null && esperado != null ? Math.max(0, esperado - pago) : null;
    return (
      <div
        data-componente="016"
        style={{
          display: 'flex', alignItems: 'flex-start', gap: 10, background: '#f97316', color: '#000',
          borderLeft: '5px solid #9a3412', borderRadius: 8, padding: '10px 14px', maxWidth: 560,
        }}
      >
        <AlertTriangle size={20} style={{ flex: '0 0 auto', marginTop: 2 }} />
        <div style={{ flex: 1, textTransform: 'none' }}>
          <div style={{ fontWeight: 800, fontSize: 14 }}>Cobrança errada — Parcelado pelo VENDEDOR</div>
          <div style={{ fontSize: 12, marginTop: 3, fontWeight: 600 }}>
            {pago != null && esperado != null
              ? <>O cliente pagou {formatMoeda(pago)} e deveria ter pago {formatMoeda(esperado)}
                  {juros != null && juros > 0 ? <> — a loja está bancando {formatMoeda(juros)} de juros.</> : '.'}</>
              : <>Não foi possível confirmar com o Mercado Pago quem bancou o juros.</>}
          </div>
          <div style={{ fontSize: 12, marginTop: 5, fontWeight: 700 }}>
            Solicite o estorno com a Administração. Esta venda não pode ser finalizada.
          </div>
        </div>
      </div>
    );
  }

  // --- FALHOU (recusado / cancelado / expirado) → tentar de novo ---
  if (status === 'recusado' || status === 'cancelado' || status === 'expirado') {
    const txt = status === 'recusado' ? 'Cartão recusado pela maquininha' : status === 'expirado' ? 'A maquininha expirou' : 'Cobrança cancelada';
    return (
      <div data-componente="016" style={cheio ? { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, padding: '12px 0' } : { marginTop: 0 }}>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: '#fef9c3', border: '1px solid #fde047', borderLeft: '5px solid #ca8a04', borderRadius: 8, padding: '8px 12px', color: '#000', fontWeight: 600, fontSize: 13, marginBottom: cheio ? 0 : 8 }}>
          {txt} — tente de novo
        </div>
        <button
          type="button" onClick={() => void cobrar()} disabled={cobrando}
          style={{
            display: cheio ? 'inline-flex' : 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            background: '#2563eb', color: '#fff', border: 0, padding: '10px 16px', borderRadius: 6, fontWeight: 600, fontSize: 14,
            cursor: cobrando ? 'not-allowed' : 'pointer', opacity: cobrando ? 0.6 : 1,
          }}
        >
          {cobrando ? <Loader2 size={16} className="animate-spin" /> : <CreditCard size={16} />} Tentar de novo
        </button>
      </div>
    );
  }

  // --- AGUARDANDO na maquininha (ou enviando o valor) ---
  if (cheio) {
    return (
      <div data-componente="016" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, padding: '8px 0' }}>
        <Loader2 size={48} className="animate-spin" style={{ color: '#15803d' }} />
        <div style={{ fontSize: 13, fontWeight: 700, color: '#15803d', background: '#dcfce7', borderRadius: 999, padding: '6px 14px' }}>
          Aguardando o cliente na maquininha…
        </div>
        <div style={{ fontSize: 11, color: 'var(--text-muted)', textAlign: 'center', maxWidth: 340 }}>
          Peça o cartão na maquininha Point. Quando aprovar, o valor fica <strong>livre</strong> pra abater na mão no Contas a Receber.
        </div>
        {cobranca && avisoEspera && (
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-color)', textAlign: 'center', maxWidth: 340 }}>
            {avisoEspera}
          </div>
        )}
      </div>
    );
  }
  // Parcelado com juros ([DA-808]): a maquininha mostra o total com juros na tela do cartão e a base SEM
  // juros na tela da senha. Duas vendas foram canceladas no balcão lendo isso como "o valor caiu".
  const parceladoComJuros = cardType === 'credit' && parcelas >= 2
    && valorNaSenha != null && totalAprovado != null && totalAprovado - valorNaSenha > 0.005;
  const cobrandoTotal = parceladoComJuros ? totalAprovado! : valor;
  return (
    <div data-componente="016" style={{ display: 'flex', alignItems: 'center', gap: 14, background: 'var(--input-bg, #f1f5f9)', border: '1px solid var(--input-border, #e5e7eb)', borderRadius: 8, padding: '10px 14px', maxWidth: 520 }}>
      <Loader2 size={22} className="animate-spin" />
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 700, fontSize: 14 }}>{cobranca ? 'Aguardando na maquininha…' : 'Enviando pra maquininha…'}</div>
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2, textTransform: 'none' }}>
          Peça o cartão ao cliente · cobrando {formatMoeda(cobrandoTotal)}{parceladoComJuros ? ` em ${parcelas}x de ${formatMoeda(cobrandoTotal / parcelas)}` : ''} · aprova sozinho quando o cliente pagar
        </div>
        {parceladoComJuros && (
          <div style={{ marginTop: 6, background: '#fde68a', color: '#000', borderRadius: 6, padding: '6px 10px', fontSize: 12, textTransform: 'none' }}>
            <div style={{ fontWeight: 800 }}>Se a maquininha perguntar, escolha PARCELADO CLIENTE.</div>
            <div style={{ fontWeight: 600, marginTop: 2 }}>
              Na tela da SENHA ela mostra {formatMoeda(valorNaSenha!)}, sem os juros — é normal, não cancele.
              O cliente paga {formatMoeda(cobrandoTotal)}.
            </div>
          </div>
        )}
        {cobranca && avisoEspera && (
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-color)', marginTop: 4, textTransform: 'none' }}>
            {avisoEspera}
          </div>
        )}
      </div>
      <button type="button" onClick={() => void cancelar()} className="btn-secundario">
        Cancelar
      </button>
    </div>
  );
}
